import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff, TrendingUp, TrendingDown } from 'lucide-react'
import { useStore } from '@/store'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface BalanceDisplayProps {
  balance: number
  change24h?: number
  change24hPercent?: number
  label?: string
  className?: string
}

export default function BalanceDisplay({ 
  balance, 
  change24h = 0,
  change24hPercent = 0,
  label = 'Total Balance',
  className 
}: BalanceDisplayProps) {
  const [isHidden, setIsHidden] = useState(false)
  
  const isPositive = change24h >= 0
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "relative overflow-hidden rounded-3xl bg-gradient-card border border-border p-6",
        className
      )}
    >
      {/* Background glow */}
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-accent-cyan/10 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        {/* Label and toggle */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-text-secondary text-sm">{label}</span>
          <button
            onClick={() => setIsHidden(!isHidden)}
            className="p-2 rounded-lg hover:bg-background-elevated transition-colors"
          >
            {isHidden ? (
              <EyeOff className="w-4 h-4 text-text-tertiary" />
            ) : (
              <Eye className="w-4 h-4 text-text-tertiary" />
            )}
          </button>
        </div>
        
        {/* Balance */}
        <div className="mb-4">
          <AnimatePresence mode="wait">
            {isHidden ? (
              <motion.div
                key="hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-balance text-text-primary"
              >
                ••••••
              </motion.div>
            ) : (
              <motion.div
                key="visible"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-balance text-text-primary tracking-tight"
              >
                {formatCurrency(balance)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* 24h change */}
        {change24h !== 0 && (
          <div className={cn(
            "flex items-center gap-2 text-sm",
            isPositive ? 'text-accent-green' : 'text-accent-red'
          )}>
            {isPositive ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            <span className="font-medium">
              {isPositive ? '+' : ''}{formatCurrency(change24h)}
            </span>
            <span className="text-text-tertiary">
              ({isPositive ? '+' : ''}{formatPercentage(change24hPercent)})
            </span>
            <span className="text-text-tertiary ml-1">24h</span>
          </div>
        )}
      </div>
    </motion.div>
  )
}